import _ from "lodash";
import Vue from "vue";
import { Graph, Workspace, ChartType } from "./viz";

import Spinner from "@/views/shared/Spinner.vue";

import { ViewingControls } from "./ViewingControls";
import { DebuggingPanel } from "./DebuggingPanel";

import { VegaTimeSeriesGraph as TimeSeriesGraph } from "./VegaTimeSeriesGraph";
import { VegaHistogram as Histogram } from "./VegaHistogram";
import { VegaRange as Range } from "./VegaRange";

import { D3Map as Map } from "./D3Map";

import Config from "@/secrets";

const Loading = Vue.extend({
    name: "Loading",
    components: {
        Spinner,
    },
    template: `<div class="viz-loading"><Spinner /></div>`,
});

export const VizGraph = Vue.extend({
    name: "VizGraph",
    components: {
        ViewingControls,
        Loading,
        DebuggingPanel,
        TimeSeriesGraph,
        Histogram,
        Range,
        // eslint-disable-next-line
        Map,
    },
    data(): {
        showScrollMessage: boolean;
        lastTouchY: number | null;
    } {
        return {
            showScrollMessage: false,
            lastTouchY: null,
        };
    },
    props: {
        viz: {
            type: Graph,
            required: true,
        },
        workspace: {
            type: Workspace,
            required: true,
        },
    },
    mounted() {
        // this.viz.log("mounted", this.viz);
    },
    updated() {
        // this.viz.log("updated", this.viz);
    },
    computed: {
        debug(): boolean {
            return false;
        },
        isDataSetEmpty(): boolean {
            return this.viz.isDataSetEmpty();
        },
    },
    methods: {
        raiseTimeZoomed(...args: unknown[]): void {
            this.$emit("viz-time-zoomed", ...args);
        },
        raiseTimeDragged(...args: unknown[]): void {
            this.$emit("viz-time-dragged", ...args);
        },
        raiseGeoZoomed(...args: unknown[]): void {
            this.$emit("viz-geo-zoomed", ...args);
        },
        raiseRemove(...args: unknown[]): void {
            this.$emit("viz-remove", ...args);
        },
        raiseCompare(...args: unknown[]): void {
            this.$emit("viz-compare", ...args);
        },
        raiseFastTime(...args: unknown[]): void {
            this.$emit("viz-fast-time", ...args);
        },
        raiseChangeSensors(...args: unknown[]): void {
            this.$emit("viz-change-sensors", ...args);
        },
        raiseChangeChart(...args: unknown[]): void {
            this.$emit("viz-change-chart", ...args);
        },
        uiNameOf(graph: Graph): string {
            if (this.viz.loadedDataSets.length == 0) {
                return "Loading";
            }

            switch (graph.chartType) {
                case ChartType.TimeSeries:
                    return "TimeSeriesGraph";
                case ChartType.Histogram:
                    return "Histogram";
                case ChartType.Range:
                    return "Range";
                case ChartType.Map:
                    return "Map";
            }

            this.viz.log("unknown chart type");
            return "TimeSeriesGraph";
        },
        handleTouchStart(event) {
            if (!Config.enableVegaMobileFeatures) return;

            if (event.touches.length === 1) {
                this.lastTouchY = event.touches[0].clientY;
                this.showScrollMessage = true;
            } else if (event.touches.length === 2) {
                this.showScrollMessage = false;
                this.lastTouchY = null;
                event.preventDefault();
            }
        },
        handleTouchEnd() {
            if (!Config.enableVegaMobileFeatures) return;

            this.showScrollMessage = false;
            this.lastTouchY = null;
        },
        handleTouchMove(event) {
            if (!Config.enableVegaMobileFeatures) return;

            if (event.touches.length === 2) {
                event.preventDefault();
            } else if (event.touches.length === 1 && this.lastTouchY !== null) {
                const currentY = event.touches[0].clientY;
                const deltaY = this.lastTouchY - currentY;

                window.scrollBy(0, deltaY);

                this.lastTouchY = currentY;
            }
        },
    },
    template: `
		<div class="viz graph" v-bind:class="{'viz-disabled': isDataSetEmpty, 'viz-scrolling-bg': showScrollMessage}">
			<ViewingControls :viz="viz" :workspace="workspace" v-bind:key="workspace.version"
				@viz-remove="raiseRemove"
				@viz-compare="raiseCompare"
				@viz-fast-time="raiseFastTime"
				@viz-time-zoomed="raiseTimeZoomed"
				@viz-geo-zoomed="raiseGeoZoomed"
				@viz-change-sensors="raiseChangeSensors"
				@viz-change-chart="raiseChangeChart" />

          <div v-show="isDataSetEmpty" class="viz-msg"> {{$tc('dataView.noData')}} </div>

          <div v-show="!isDataSetEmpty && showScrollMessage" class="viz-msg viz-msg--data-area-only"> {{$tc('dataView.scrollMsg')}} </div>

          <div @touchstart="handleTouchStart" @touchend="handleTouchEnd" @touchmove="handleTouchMove">
            <component v-bind:is="uiNameOf(viz)" :viz="viz" :workspace="workspace"
                       @viz-geo-zoomed="raiseGeoZoomed"
                       @viz-time-zoomed="raiseTimeZoomed"
                       @viz-time-dragged="raiseTimeDragged" />
          </div>

            <DebuggingPanel :viz="viz" :workspace="workspace" v-if="debug" />
		</div>
	`,
});
